import React from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemGroup,
  IonLabel,
  IonPage,
  IonPopover,
  IonTitle,
  IonToolbar,
  isPlatform,
  useIonModal,
} from "@ionic/react";
import {
  cloudDownloadOutline,
  downloadSharp,
  informationCircle,
  trash,
} from "ionicons/icons";
import { SpikeGenImagesProps } from "../../interfaces/core";
import getSize from "../../lib/getImageSize";
import { useState } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const SpikeGenImage: React.FC<{
  onDelete: React.MouseEventHandler<HTMLIonButtonElement>;
  image: SpikeGenImagesProps;
  setIsLoading: (e: boolean) => void;
}> = ({
  onDelete,
  setIsLoading,
  image: { attributesList, full, id, preview, statsList },
}) => {
  const [present, dismiss] = useIonModal(SpikeGenImageInfo, {
    dismiss: () => dismiss(),
    image: { attributesList, statsList, full },
  });
  const onDownload = async () => {
    setIsLoading(true);
    const dlImg = new Image();
    dlImg.src = full;
    await dlImg.decode();
    const link = document.createElement("a");
    link.href = dlImg.src;
    link.download = `Spike Pie ${attributesList[0].name} ${attributesList[1].name} ${attributesList[2].name}.jpg`;
    link.click();
    setIsLoading(false);
  };
  const mobileScreen = isPlatform("mobile") && !isPlatform("tablet");
  const onInfo = () => {
    mobileScreen
      ? present({
          initialBreakpoint: 0.5,
          breakpoints: [0, 0.5, 1],
          backdropDismiss: true,
        })
      : present({
          swipeToClose: true,
        });
  };
  return (
    <div className="relative group m-4">
      <IonButton
        color="transparent"
        className="absolute z-[2] top-3 right-3 opacity-0 pointer-touch:opacity-50 group-hover:opacity-100 transition-opacity [--box-shadow:none]"
        onClick={onInfo}
        size="small"
      >
        <IonIcon icon={informationCircle} />
      </IonButton>
      <IonButton
        color="transparent"
        className="absolute z-[2] left-3 bottom-3 opacity-0 pointer-touch:opacity-50 group-hover:opacity-100 transition-opacity [--box-shadow:none]"
        onClick={onDelete}
        size="small"
      >
        <IonIcon icon={trash} />
      </IonButton>
      <div className="bg-white dark:bg-zinc-700/70 w-full h-full border-[14px] border-black dark:border-zinc-200 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.5)_inset,2px_4px_18px_2px_rgba(0,0,0,0.3)]">
        <div className={`pt-[15%] pb-[15%] pr-[13%] pl-[13%] relative`}>
          <div className="w-full after:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.4)_inset] after:inset-0 after:block after:z-[3] after:absolute after:mt-[15%] after:mb-[15%] after:mr-[13%] after:ml-[13%]">
            <img
              src={preview}
              className="w-full z-[1] relative opacity-100"
              alt=""
            />
          </div>
        </div>
      </div>
      <IonButton
        color="transparent"
        className="absolute z-[2] right-3 bottom-3 opacity-0 pointer-touch:opacity-50 group-hover:opacity-100 transition-opacity [--box-shadow:none]"
        onClick={onDownload}
        size="small"
      >
        <IonIcon icon={cloudDownloadOutline} md={downloadSharp} />
      </IonButton>
    </div>
  );
};
const ColorCircle = ({ color }) => {
  let hex = [];
  if (color.includes("-")) {
    hex = color.split("-");
  } else {
    hex = [color];
  }
  return (
    <div
      className="w-6 h-6 rounded-full"
      style={{
        background: color.includes("-")
          ? `linear-gradient(270deg, ${hex[0]} 0%, ${hex[1]} 100%)`
          : hex[0],
      }}
    />
  );
};
const ColorPopover = ({ color }) => {
  let hex = [];
  const isGradient = color.includes("-");
  if (isGradient) {
    hex = color.split("-");
  } else {
    hex = [color];
  }
  return (
    <div
      className="w-full h-16 rounded-lg flex justify-between items-center px-4 uppercase"
      style={{
        background: isGradient
          ? `linear-gradient(270deg, ${hex[0]} 0%, ${hex[1]} 100%)`
          : hex[0],
      }}
    >
      <p className="bg-black/20 text-white py-0.5 px-2 rounded-md text-sm font-mono font-semibold">
        {isGradient ? hex[0] : hex}
      </p>
      {isGradient && (
        <p className="bg-black/20 text-white py-0.5 px-2 rounded-md text-sm font-mono font-semibold">
          {hex[1]}
        </p>
      )}
    </div>
  );
};
const SpikeGenImageInfo = ({ dismiss, image }) => {
  const { t } = useTranslation();
  const { statsList, attributesList, full }: SpikeGenImagesProps = image;
  const [size, setSize] = useState(0);
  const segmentSize = 20;
  const [statsSegment, setstatsSegment] = useState(
    statsList.slice(0, segmentSize)
  );
  const [hasMore, setHasMore] = useState(true);
  const [popover, setPopover] = useState({
    open: false,
    color: null,
    event: undefined,
  });
  const getImageSize = async () => {
    const res = await getSize(full);
    const value = Math.floor((parseInt(res) / 1024) * 100) / 100;
    setSize(value);
  };

  const handleLoadMore = () => {
    const newNum = statsSegment.length + segmentSize;
    const noMore = newNum >= statsList.length;
    const newList = statsSegment.concat(
      statsList.slice(statsSegment.length, newNum)
    );
    if (noMore) {
      setHasMore(false);
    }
    setstatsSegment(newList);
  };
  const handlePopover = (e, color) => {
    setPopover({ open: true, color, event: e.nativeEvent });
  };
  useEffect(() => {
    getImageSize();
  }, []);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="primary">
            <IonButton onClick={dismiss} className="!font-semibold">
              {t(`Done`)}
            </IonButton>
          </IonButtons>
          <IonTitle>{t(`Info`)}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonItemGroup className="item-group-input !bg-transparent margin">
          {attributesList.map(({ name, type }, i) => (
            <IonItem key={`attr.${name}.${i}`} className="item-input">
              <IonLabel className="capitalize">
                {type.toLocaleLowerCase()}
              </IonLabel>
              <IonLabel slot="end" className="capitalize">
                {name}
              </IonLabel>
            </IonItem>
          ))}
          <IonItem className="item-input">
            <IonLabel className="capitalize">{t(`Size`)}</IonLabel>
            <IonLabel slot="end">{size} MB</IonLabel>
          </IonItem>
        </IonItemGroup>
        <IonItemGroup className="item-group-input !bg-transparent margin">
          {statsSegment.map(({ name, from, to }) => (
            <IonItem
              key={`stat.${name}`}
              className="item-input"
              onClick={(e) => handlePopover(e, name)}
            >
              <IonLabel>
                <div className="flex gap-2 text-sm uppercase font-bold items-center font-mono">
                  <ColorCircle color={name} />
                  <span className="opacity-85">{name}</span>
                </div>
              </IonLabel>
              <IonLabel slot="end">
                {from} of {to}
              </IonLabel>
            </IonItem>
          ))}
          {hasMore && (
            <IonItem onClick={handleLoadMore} button detail={false}>
              {t(`Load More`)}
            </IonItem>
          )}
          <IonPopover
            size="cover"
            isOpen={popover.open}
            event={popover.event}
            onDidDismiss={() =>
              setPopover({ open: false, color: null, event: undefined })
            }
            alignment="start"
            className="rounded-lg"
          >
            <ColorPopover color={popover.color} />
          </IonPopover>
        </IonItemGroup>
      </IonContent>
    </IonPage>
  );
};

export default SpikeGenImage;
